import React, { useState, useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../auth-context';
// use PUBLIC_URL so path works even when app is served from a subpath
const LOGO = (process.env.PUBLIC_URL || "") + "/digitallogo.png";
import {
  TextField, Button, Container, Box, Alert, CircularProgress,
  InputAdornment, IconButton, Paper, Typography, Card, CardContent,
  Fade, Zoom, useMediaQuery, Avatar, Divider, Stack, alpha, styled
} from "@mui/material";
import {
  Visibility, VisibilityOff, Login as LoginIcon, Security,
  AdminPanelSettings, Person, Lock, CheckCircle
} from '@mui/icons-material';
import appTheme from '../theme';
import { collection, getDocs, query, where, addDoc } from 'firebase/firestore';
import { db } from '../firebase-config';

// **ENHANCED STYLED COMPONENTS WITH LUXURY THEME**
const LoginContainer = styled(Box)({
  minHeight: '100vh',
  background: appTheme.gradients?.luxury || appTheme.colors.surfaceLuxury,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: appTheme.spacing?.xl || '32px',
  position: 'relative',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: appTheme.gradients?.shimmer || 'transparent',
    opacity: 0.1,
    pointerEvents: 'none',
  }
});

const LoginCard = styled(Card)({
  maxWidth: 480,
  width: '100%',
  borderRadius: appTheme.borderRadius?.rounded || '16px',
  boxShadow: appTheme.shadows?.dramatic || '0 16px 40px rgba(0,0,0,0.12)',
  border: `1px solid ${appTheme.colors?.borderSubtle || '#f4f6f7'}`,
  overflow: 'hidden',
  position: 'relative',
  background: appTheme.colors?.surface || '#ffffff',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '6px',
    background: appTheme.gradients?.hero || appTheme.colors.primary,
  }
});

const HeaderSection = styled(Box)({
  background: appTheme.gradients?.cardLuxury || appTheme.colors.surfaceElevated,
  padding: appTheme.spacing?.xl || '32px',
  textAlign: 'center',
  position: 'relative',
});

const StyledTextField = styled(TextField)({
  '& .MuiOutlinedInput-root': {
    borderRadius: appTheme.borderRadius?.medium || '10px',
    backgroundColor: appTheme.colors?.surface || '#ffffff',
    transition: appTheme.transitions?.luxury || '400ms cubic-bezier(0.4, 0, 0.2, 1)',
    '&:hover': {
      backgroundColor: appTheme.colors?.surfaceElevated || '#f7e7ce',
      '& fieldset': {
        borderColor: appTheme.colors?.primary || '#1a4d3a',
        borderWidth: 2,
      },
    },
    '&.Mui-focused': {
      backgroundColor: appTheme.colors?.surface || '#ffffff',
      boxShadow: appTheme.shadows?.goldGlow || '0 4px 20px rgba(212, 175, 55, 0.15)',
      '& fieldset': {
        borderColor: appTheme.colors?.accent || '#d4af37',
        borderWidth: 2,
      },
    },
  },
  '& .MuiInputLabel-root': {
    fontWeight: appTheme.typography?.fontWeights?.medium || 500,
    '&.Mui-focused': {
      color: appTheme.colors?.accent || '#d4af37',
    },
  },
  '& .MuiInputAdornment-root': {
    '& .MuiSvgIcon-root': {
      color: appTheme.colors?.primary || '#1a4d3a',
    }
  }
});

const LoginButton = styled(Button)({
  background: appTheme.gradients?.hero || appTheme.colors.primary,
  color: appTheme.colors?.champagne || '#f7e7ce',
  borderRadius: appTheme.borderRadius?.rounded || '16px',
  padding: `${appTheme.spacing?.md || '20px'} ${appTheme.spacing?.xl || '32px'}`,
  fontSize: appTheme.typography?.fontSizes?.large || '20px',
  fontWeight: appTheme.typography?.fontWeights?.bold || 700,
  textTransform: 'none',
  height: 56,
  boxShadow: appTheme.shadows?.medium || '0 4px 12px rgba(0,0,0,0.08)',
  transition: appTheme.transitions?.luxury || '400ms cubic-bezier(0.4, 0, 0.2, 1)',
  '&:hover': {
    background: appTheme.gradients?.heroReversed || appTheme.colors.secondary,
    boxShadow: appTheme.shadows?.strong || '0 8px 20px rgba(0,0,0,0.1)',
    transform: 'translateY(-2px)',
  },
  '&:disabled': {
    background: appTheme.colors?.borderSubtle || '#f4f6f7',
    color: appTheme.colors?.textMuted || '#8b7d73',
    transform: 'none',
  },
});

const WelcomeCard = styled(Card)({
  marginBottom: appTheme.spacing?.lg || '24px',
  borderRadius: appTheme.borderRadius?.medium || '10px',
  border: `1px solid ${appTheme.colors?.borderSubtle || '#f4f6f7'}`,
  background: alpha(appTheme.colors?.jadeFog || '#e6f2e6', 0.3),
});

const RoleChip = styled(Box)({
  display: 'inline-flex',
  alignItems: 'center',
  gap: appTheme.spacing?.xs || '8px',
  padding: `${appTheme.spacing?.xs || '8px'} ${appTheme.spacing?.sm || '12px'}`,
  borderRadius: appTheme.borderRadius?.rounded || '16px',
  fontSize: appTheme.typography?.fontSizes?.small || '14px',
  fontWeight: appTheme.typography?.fontWeights?.bold || 700,
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
});

const LoginScreen = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [mounted, setMounted] = useState(false);

  const { login } = useContext(AuthContext);
  const navigate = useNavigate();
  const isMobile = useMediaQuery('(max-width:600px)');
  const isTablet = useMediaQuery('(max-width:900px)');

  // **MOUNT ANIMATION**
  useEffect(() => {
    setMounted(true);
  }, []);

  // **DEMO USERS SETUP**
  useEffect(() => {
    const addInitialAdminUsers = async () => {
      try {
        const usersCollection = collection(db, 'adminUsers');
        const snapshot = await getDocs(usersCollection);

        if (snapshot.empty) {
          const initialUsers = [
            { username: 'admin', password: 'admin123', role: 'admin', name: 'System Administrator' },
            { username: 'superadmin', password: 'superadmin123', role: 'superadmin', name: 'Super Administrator' },
            { username: 'chef', password: 'chef123', role: 'chef', name: 'Restaurant chef' }
          ];

          for (const user of initialUsers) {
            await addDoc(usersCollection, user);
          }
          console.log('✅ Initial admin users added to Firestore');
        }
      } catch (error) {
        console.error('❌ Error adding initial admin users:', error);
      }
    };

    // Uncomment to run once
    // addInitialAdminUsers();
  }, []);

  // **ENHANCED LOGIN HANDLER**
  const handleLogin = async () => {
    setError('');

    if (!username.trim() || !password.trim()) {
      setError('Username and password are required');
      return;
    }

    setLoading(true);

    try {
      const usersCollection = collection(db, 'adminUsers');
      const q = query(
        usersCollection,
        where('username', '==', username.trim().toLowerCase()),
        where('password', '==', password.trim())
      );

      const querySnapshot = await getDocs(q);

      if (!querySnapshot.empty) {
        const userData = querySnapshot.docs[0].data();
        const userId = querySnapshot.docs[0].id;

        if (['admin', 'superadmin', 'chef'].includes(userData.role)) {
          const loginSuccess = await login({
            username: userData.username,
            role: userData.role,
            name: userData.name || userData.username,
            id: userId
          });

          if (loginSuccess) {
            // Add a small delay for better UX
            setTimeout(() => {
              navigate('/dashboard');
            }, 500);
          } else {
            setError('Login failed. Please try again.');
          }
        } else {
          setError('Access denied. Contact Administrator for permission.');
        }
      } else {
        setError('Invalid username or password. Please check your credentials.');
      }
    } catch (error) {
      console.error('Login error:', error);
      setError('Connection error. Please check your internet connection and try again.');
    } finally {
      setLoading(false);
    }
  };

  // **HANDLE ENTER KEY**
  const handleKeyPress = (event) => {
    if (event.key === 'Enter' && !loading) {
      handleLogin();
    }
  };

  // **GET ROLE STYLING**
  const getRoleConfig = (role) => {
    switch (role) {
      case 'superadmin':
        return {
          icon: <AdminPanelSettings />,
          color: appTheme.colors?.error || '#c0392b',
          label: 'Super Admin'
        };
      case 'admin':
        return {
          icon: <Security />,
          color: appTheme.colors?.primary || '#1a4d3a',
          label: 'Administrator'
        };
      case 'chef':
        return {
          icon: <Person />,
          color: appTheme.colors?.secondary || '#8b1538',
          label: 'chef'
        };
      default:
        return {
          icon: <Person />,
          color: appTheme.colors?.textMuted || '#8b7d73',
          label: 'User'
        };
    }
  };

  return (
    <LoginContainer>
      <Container maxWidth="sm">
        <Fade in={mounted} timeout={800}>
          <Box>
            {/* **WELCOME CARD** */}
            <Zoom in={mounted} timeout={600}>
              <WelcomeCard>
                <CardContent sx={{ textAlign: 'center', py: 3 }}>
                  <Typography
                    variant="h4"
                    sx={{
                      fontWeight: appTheme.typography?.fontWeights?.black || 900,
                      color: appTheme.colors?.primary || '#1a4d3a',
                      mb: 1,
                    }}
                  >
                    Welcome Back!
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{
                      color: appTheme.colors?.textSecondary || '#5d6d7e',
                      fontWeight: appTheme.typography?.fontWeights?.medium || 500,
                    }}
                  >
                    Sign in to access your restaurant management dashboard
                  </Typography>
                </CardContent>
              </WelcomeCard>
            </Zoom>

            {/* **MAIN LOGIN CARD** */}
            <Zoom in={mounted} timeout={1000}>
              <LoginCard>
                {/* **HEADER SECTION** */}
                <HeaderSection>
                  <Box sx={{ mb: 4, display: 'flex', justifyContent: 'center' }}>
                    <img
                      src={LOGO}
                      alt="Logo"
                      onError={(e) => { e.target.style.display = 'none'; }}
                      style={{
                        width: '220px',
                        height: 'auto',
                        objectFit: 'contain',
                      }}
                    />
                  </Box>

                  <Typography
                    variant="h3"
                    sx={{
                      fontWeight: appTheme.typography?.fontWeights?.black || 900,
                      color: appTheme.colors?.primary || '#1a4d3a',
                      mb: 1,
                    }}
                  >
                    Admin Portal
                  </Typography>

                  <Typography
                    variant="h6"
                    sx={{
                      color: appTheme.colors?.textSecondary || '#5d6d7e',
                      fontWeight: appTheme.typography?.fontWeights?.medium || 500,
                    }}
                  >
                    Secure access to management tools
                  </Typography>
                </HeaderSection>

                {/* **FORM SECTION** */}
                <CardContent sx={{ padding: appTheme.spacing?.xl || '32px' }}>
                  <Stack spacing={3}>
                    {/* **USERNAME FIELD** */}
                    <StyledTextField
                      label="Username"
                      variant="outlined"
                      fullWidth
                      value={username}
                      onChange={(e) => setUsername(e.target.value)}
                      onKeyPress={handleKeyPress}
                      disabled={loading}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Person sx={{ color: appTheme.colors?.primary || '#1a4d3a' }} />
                          </InputAdornment>
                        ),
                      }}
                      placeholder="Enter your username"
                    />

                    {/* **PASSWORD FIELD** */}
                    <StyledTextField
                      label="Password"
                      type={showPassword ? 'text' : 'password'}
                      variant="outlined"
                      fullWidth
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      onKeyPress={handleKeyPress}
                      disabled={loading}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Lock sx={{ color: appTheme.colors?.primary || '#1a4d3a' }} />
                          </InputAdornment>
                        ),
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              onClick={() => setShowPassword(!showPassword)}
                              edge="end"
                              sx={{ color: appTheme.colors?.accent || '#d4af37' }}
                            >
                              {showPassword ? <VisibilityOff /> : <Visibility />}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                      placeholder="Enter your password"
                    />

                    {/* **ERROR ALERT** */}
                    {error && (
                      <Fade in={!!error}>
                        <Alert
                          severity="error"
                          sx={{
                            borderRadius: appTheme.borderRadius?.medium || '10px',
                            backgroundColor: alpha(appTheme.colors?.error || '#c0392b', 0.1),
                            color: appTheme.colors?.error || '#c0392b',
                            border: `1px solid ${alpha(appTheme.colors?.error || '#c0392b', 0.3)}`,
                            fontWeight: appTheme.typography?.fontWeights?.medium || 500,
                            '& .MuiAlert-icon': {
                              color: appTheme.colors?.error || '#c0392b',
                            }
                          }}
                        >
                          {error}
                        </Alert>
                      </Fade>
                    )}

                    {/* **LOGIN BUTTON** */}
                    <LoginButton
                      fullWidth
                      onClick={handleLogin}
                      disabled={loading || !username.trim() || !password.trim()}
                      startIcon={loading ? null : <LoginIcon />}
                    >
                      {loading ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <CircularProgress
                            size={24}
                            sx={{ color: appTheme.colors?.champagne || '#f7e7ce' }}
                          />
                          <Typography variant="body1" sx={{ fontWeight: 600 }}>
                            Signing in...
                          </Typography>
                        </Box>
                      ) : (
                        'Sign In to Dashboard'
                      )}
                    </LoginButton>
                  </Stack>
                </CardContent>

                {/* **DEMO CREDENTIALS SECTION** */}
                {/* <Box sx={{ p: 3, backgroundColor: alpha(appTheme.colors?.goldenHour || '#faf6e8', 0.5) }}>
                  <Typography
                    variant="h6"
                    sx={{
                      color: appTheme.colors?.primary || '#1a4d3a',
                      fontWeight: appTheme.typography?.fontWeights?.bold || 700,
                      mb: 2,
                      textAlign: 'center',
                    }}
                  >
                    Demo Credentials
                  </Typography>

                  <Stack spacing={2}>
                    {[
                      { username: 'superadmin', password: 'superadmin123', role: 'superadmin' },
                      { username: 'admin', password: 'admin123', role: 'admin' },
                      { username: 'chef', password: 'chef123', role: 'chef' },
                    ].map((demo) => {
                      const config = getRoleConfig(demo.role);
                      return (
                        <Card
                          key={demo.username}
                          sx={{
                            cursor: 'pointer',
                            border: `1px solid ${appTheme.colors?.borderSubtle || '#f4f6f7'}`,
                            borderRadius: appTheme.borderRadius?.soft || '6px',
                            transition: appTheme.transitions?.smooth || '300ms ease-out',
                            '&:hover': {
                              boxShadow: appTheme.shadows?.soft || '0 2px 6px rgba(0,0,0,0.06)',
                              transform: 'translateY(-1px)',
                            }
                          }}
                          onClick={() => {
                            setUsername(demo.username);
                            setPassword(demo.password);
                            setError('');
                          }}
                        >
                          <CardContent sx={{ p: 2 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <Avatar
                                  sx={{
                                    width: 32,
                                    height: 32,
                                    backgroundColor: config.color,
                                    color: 'white',
                                  }}
                                >
                                  {config.icon}
                                </Avatar>
                                <Box>
                                  <Typography variant="body1" sx={{ fontWeight: 600 }}>
                                    {demo.username}
                                  </Typography>
                                  <Typography variant="body2" color="textSecondary">
                                    {demo.password}
                                  </Typography>
                                </Box>
                              </Box>

                              <RoleChip
                                sx={{
                                  backgroundColor: alpha(config.color, 0.1),
                                  color: config.color,
                                }}
                              >
                                {config.icon}
                                {config.label}
                              </RoleChip>
                            </Box>
                          </CardContent>
                        </Card>
                      );
                    })}
                  </Stack>

                  <Box sx={{ mt: 2, textAlign: 'center' }}>
                    <Typography variant="caption" color="textSecondary">
                      Click any credential card above to auto-fill the login form
                    </Typography>
                  </Box>
                </Box> */}
              </LoginCard>
            </Zoom>

            {/* **FOOTER INFO** */}
            <Fade in={mounted} timeout={1200}>
              <Box sx={{ textAlign: 'center', mt: 3 }}>
                <Typography
                  variant="body2"
                  sx={{
                    color: appTheme.colors?.textMuted || '#8b7d73',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 1,
                  }}
                >
                  <Security fontSize="small" />
                  Secured by enterprise-grade encryption
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    color: appTheme.colors?.textMuted || '#8b7d73',
                    mt: 1,
                    display: 'block',
                  }}
                >
                  Restaurant Management System v2.0 • {new Date().getFullYear()}
                </Typography>
              </Box>
            </Fade>
          </Box>
        </Fade>
      </Container>
    </LoginContainer>
  );
};

export default LoginScreen;
